import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { coaSyncApi, TemplateDiff } from '../../utils/api/coasync';
import { TypeBadge } from './AccountTypeBadge';

// ── "Sync from template": a preview first, an administrator approves (batch 5 round 7) ──────────────
// COA-4 for the live 40-account house template, and the customer's §5: adding template accounts to a
// chart that already exists is shown first and approved by an administrator (O7: the administrator
// alone). Nothing is added by opening this; only ticking accounts and pressing Add selected adds them.
//
// ⛔ The screen decides nothing. Which template accounts are missing, which ones cannot be added because
// another live account holds the code, and whether this login may approve (canApprove) all come from
// GET /accounting/chart/template-diff (server/src/services/accountingSetup.ts templateDiff), and the
// server checks all three again when the selection is posted. A template account this chart removed or
// renumbered is in neither list — it is never offered again.

const CLASS_KEY: Record<string, string> = {
  asset: 'pa_chart_class_asset', liability: 'pa_chart_class_liability', equity: 'pa_chart_class_equity',
  income: 'pa_chart_class_income', expense: 'pa_chart_class_expense',
};
const btn: React.CSSProperties = {
  padding: '5px 10px', fontSize: 12, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};

export default function TemplateSync({ clientId, onChanged, onClose }: { clientId: string; onChanged: () => Promise<void> | void; onClose: () => void }) {
  const { t } = useTranslation();
  const [diff, setDiff] = useState<TemplateDiff | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [added, setAdded] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await coaSyncApi.diff(clientId);
      setDiff(d);
      // A tick on an account the server no longer offers is dropped, never sent.
      setPicked(p => new Set(Array.from(p).filter(id => d.missing.some(m => m.templateId === id))));
    } catch (e: any) { setErr(e.message || String(e)); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const can = !!diff?.canApprove;
  // A login that may not approve takes no tick at all — the boxes are disabled, and this holds even for a
  // click that reaches a disabled box (some environments still deliver one).
  const toggle = (id: string) => { if (!can || busy) return; setPicked(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const addSelected = async () => {
    if (!can || !picked.size) return;
    setBusy(true); setErr(''); setAdded(null);
    try {
      const r = await coaSyncApi.add(clientId, Array.from(picked));
      setAdded(Number(r.copied) || 0);
      setPicked(new Set());
      await onChanged();
    } catch (e: any) { setErr(e.message || String(e)); }
    finally { await load(); setBusy(false); }
  };

  return (
    <div data-template-sync style={{ marginBottom: 10, padding: 10, borderRadius: 8, background: 'var(--bg-app)', border: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <i className="ti ti-copy" style={{ fontSize: 15, color: 'var(--accent)' }} aria-hidden />
        <span style={{ flex: 1, fontSize: 12.5, fontWeight: 800, color: 'var(--text-primary)' }}>{t('coas_title')}</span>
        <button data-sync-close onClick={onClose} style={{ ...btn, padding: '3px 8px', fontSize: 11 }}>{t('coas_close')}</button>
      </div>
      {err && <div data-sync-error style={{ fontSize: 12, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9', borderRadius: 6, padding: '6px 9px', marginBottom: 6 }}>{err}</div>}
      {added !== null && <div data-sync-added style={{ fontSize: 12, color: '#047857', marginBottom: 6 }}>{t('coas_added', { n: added })}</div>}
      {!diff && !err && <div data-sync-loading style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('coas_loading')}</div>}
      {diff && (
        <>
          {!diff.missing.length ? (
            <div data-sync-nothing style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('coas_nothing')}</div>
          ) : (
            <>
              <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginBottom: 6 }}>{t('coas_intro')}</div>
              {!can && (
                <div data-sync-admin-only style={{ fontSize: 11.5, color: '#92400E', background: '#FEF6E7', border: '1px solid #F5D9A8', borderRadius: 6, padding: '5px 8px', marginBottom: 6 }}>
                  <i className="ti ti-lock" aria-hidden /> {t('coas_admin_only')}
                </div>
              )}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>
                  {diff.missing.map(m => (
                    <tr key={m.templateId} data-sync-missing={m.code} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '4px 6px', width: 24 }}>
                        <input type="checkbox" data-sync-pick={m.templateId} checked={picked.has(m.templateId)} disabled={!can || busy}
                          onChange={() => toggle(m.templateId)} aria-label={t('coas_pick', { code: m.code, name: m.nameTh })} />
                      </td>
                      <td style={{ padding: '4px 6px', width: 56, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{m.code}</td>
                      <td style={{ padding: '4px 6px' }}>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{m.nameTh}</span>
                        <span style={{ marginLeft: 8, color: 'var(--text-tertiary)' }}>{m.nameEn}</span>
                      </td>
                      <td style={{ padding: '4px 6px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{t(CLASS_KEY[m.cls] || m.cls)}</td>
                      <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}><TypeBadge type={m.type} kind={m.kind} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: 'flex', gap: 7, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <button data-sync-all disabled={!can || busy} onClick={() => setPicked(new Set(diff.missing.map(m => m.templateId)))} style={btn}>{t('coas_select_all')}</button>
                <button data-sync-none disabled={!can || busy || !picked.size} onClick={() => setPicked(new Set())} style={btn}>{t('coas_select_none')}</button>
                <button data-sync-add disabled={!can || busy || !picked.size} onClick={addSelected} title={!can ? t('coas_admin_only') : undefined}
                  style={{ ...btn, background: can && picked.size ? 'var(--accent)' : 'var(--bg-surface)', color: can && picked.size ? '#fff' : 'var(--text-tertiary)', border: can && picked.size ? 'none' : btn.border }}>
                  <i className={busy ? 'ti ti-loader' : 'ti ti-plus'} style={{ display: 'inline-block', animation: busy ? 'spin 0.7s linear infinite' : undefined }} aria-hidden /> {t('coas_add_selected', { n: picked.size })}
                </button>
              </div>
            </>
          )}
          {!!diff.conflicts.length && (
            <div data-sync-conflicts style={{ marginTop: 10, borderTop: '1px dashed var(--border-strong)', paddingTop: 7 }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--text-secondary)' }}>{t('coas_conflicts_title', { n: diff.conflicts.length })}</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: '2px 0 4px' }}>{t('coas_conflicts_tip')}</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>
                  {diff.conflicts.map(c => (
                    <tr key={c.templateId} data-sync-conflict={c.code} style={{ borderTop: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                      <td style={{ padding: '4px 6px', width: 24 }} />
                      <td style={{ padding: '4px 6px', width: 56, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{c.code}</td>
                      <td style={{ padding: '4px 6px' }}>
                        <span style={{ fontWeight: 600 }}>{c.nameTh}</span>
                        <span style={{ marginLeft: 8, color: 'var(--text-tertiary)' }}>{c.nameEn}</span>
                      </td>
                      <td data-sync-held-by={c.occupiedBy.id} style={{ padding: '4px 6px', fontSize: 11.5 }}>
                        {t('coas_held_by', { code: c.occupiedBy.code, name: c.occupiedBy.nameTh })}
                        <span style={{ marginLeft: 6, color: 'var(--text-tertiary)' }}>({c.occupiedBy.templateId ? t('coas_held_copy') : t('coas_held_own')})</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
